import { FormController } from '$lib/client/actions';
import type { ComponentA, FormConfig, SectionAll } from "$lib/config";
import _ from "lodash";
import { REST } from "./conn";
import Modal from '$lib/client/components/Modal.svelte';
import type LoadingBackdrop from "./components/LoadingBackdrop.svelte";
import { PUBLIC_ENVIRONMENT } from '$env/static/public';
import { Embed } from './wordpress';
import { DateTime } from 'luxon';
import { ulid } from 'ulid';

export class DebugSettings {
    static debug_enabled = PUBLIC_ENVIRONMENT == 'dev';
    static save_to_mailchimp: boolean = true;
    static show_debug: boolean = this.debug_enabled;
    static save_to_db: boolean = true;
}

export class KTIForm {
    static form_config: FormConfig;
    static error_modal: Modal;
    static form_data: FormData;
    //let formData: { [key: string]: string } = {};    //FRom page.svelte...
    static server_validations: { [component_id: string]: ServerDataValidation } = {};
    static loading_backdrop: LoadingBackdrop
    static is_in_wordpress: boolean = false;
    static show_success_page: boolean = false;
    static processing_submission: boolean = false;
    static redirecting: boolean = false;

    // Static init function
    static init() {
        Embed.init(KTIForm.form_config.URLName, () => {
            //When connected to wordpress, set in_wordpress to true
            KTIForm.is_in_wordpress = true;
        })
        KTIForm.form_data = new FormData();
        KTIForm.server_validations = {};
        FormController.set_form_def(KTIForm.form_config);
    }

    static addServerValidation(what: string, component_id: string): ServerDataValidation {
        if (component_id in KTIForm.server_validations) {
            return KTIForm.server_validations[component_id];
        }
        KTIForm.server_validations[component_id] = new ServerDataValidation(what, component_id);
        return KTIForm.server_validations[component_id];
    }

    // Used when section type is stepper and must be completed before going to the next section
    static async isSectionValid(section: SectionAll): Promise<true | ComponentA> {
        for (let compConfig of section.Components) {
            //reference to Svelte Component
            let c = compConfig.Comp;
            if (!c) {
                continue;
            }
            if (!('validate' in c)) {
                console.error('validate method not found for:', c);
                continue;
            }
            if (!('appendData' in c)) {
                console.error('appendData method not found for:', c);
                continue;
            }
            let valid = await c.validate();
            if (!valid && compConfig.Required) {
                console.log('invalid_comps:', compConfig);
                return compConfig;
            }
        }
        return true;
    }

    static async getInvalidComponents(): Promise<any[]> {
        let invalid_comps = []
        for (let section of KTIForm.form_config.Sections) {
            for (let compConfig of section.Components) {
                //reference to Svelte Component
                let c = compConfig.Comp;
                if (!c || compConfig.Type === 'CenPOS') {
                    continue;
                }
                //Skip if hidden
                if (FormController.hidden_components.includes(compConfig.id)) {
                    continue;
                }
                if (!('validate' in c)) {
                    console.error('validate method not found for:', c);
                    continue;
                }
                if (!('appendData' in c)) {
                    console.error('appendData method not found for:', c);
                    continue;
                }
                console.log('validating:', c);
                let valid = await c.validate();
                if (valid) {
                    continue;
                } else if (compConfig.Required) {
                    //not valid AND required
                    invalid_comps.push(c);
                } else if (c.Value != '' && c.Value === undefined && c.Value === null) {
                    invalid_comps.push(c);
                }
            }
        }
        return invalid_comps;
    }

    static async getFormData(): Promise<boolean> {
        console.log('getFormData()', KTIForm.form_config.Sections);
        for (let section of KTIForm.form_config.Sections) {
            for (let compConfig of section.Components) {
                let c = compConfig.Comp;
                //let valid = await c.validate();
                // if (!valid) {
                //     console.error('Invalid component:', c);
                //     return false;
                // }
                //if (valid) {
                try {
                    c.appendData(KTIForm.form_data);
                } catch (err) {
                    console.error(err);
                    throw err;
                }
                //}
            }
        }
        return true
    }

    // Static addComponent method
    // static addComponent(component: SvelteComponent) {
    //     component.$on("update", (event) => {
    //         // Add update handling logic here
    //     });
    // }

    static _validations(): ServerDataValidation[] {
        return Object.values(KTIForm.server_validations);
    }

    static validating(): boolean {
        return KTIForm._validations().some((v) => v.sending);
    }

    static whichValidating(): string[] {
        return KTIForm._validations().filter((v) => v.sending).map((v) => v.what);
    }

    static async failedValidation(): Promise<boolean> {
        const startTime = DateTime.now();

        if (KTIForm.failedServiceCheck()) {
            for (let validator of KTIForm._validations()) {
                if (validator.reason === 'Network error') {
                    validator.validate_value(validator.value)
                }
            }
            while (DateTime.now().diff(startTime).toMillis() < 10000 && KTIForm.failedServiceCheck()) {
                await new Promise(resolve => setTimeout(resolve, 100))
            }
        }

        return KTIForm._validations().some((v) => !v.valid);
    }

    static failedDupSubmissionCheck(): boolean {
        return KTIForm._validations().some((v) => v.reason === 'submissionCheck');
    }
    static failedProductCheck(): boolean {
        return KTIForm._validations().some((v) => v.reason === 'productCheck');
    }
    static failedServiceCheck(): boolean {
        return KTIForm._validations().some((v) => v.reason === 'Network error');
    }
    static failedInstanceCheck(): boolean {
        return KTIForm._validations().some((v) => v.reason === 'instanceCheck');
    }
    static toString(): string {
        return KTIForm._validations().map((v) => `${v.what}: ${v.valid}`).join(', ');
    }
}

export class ServerDataValidation {
    requestID: string = '';
    validated: boolean;
    sending: boolean;
    valid: boolean;
    value: any; //The value to be validated
    reason: string;
    what: string;
    component_id: string;
    constructor(what: string, component_id: string) {
        this.what = what;
        this.component_id = component_id;
        this.value = null;
        this.sending = false;
        this.reset_validation();
        //ServerDataValidation.validation[what] = this;
    }
    reset_validation() {
        this.validated = false;
        this.valid = false;
        this.reason = '';
    }
    async validate_value(value: any) {
        //let this: ServerDataValidation = KTIForm.validations[this.what];
        if ((this.validated || this.sending) && _.isEqual(this.value, value)) {
            console.log('Already validated', this.valid)
            return this.valid;
        }

        this.reset_validation();

        let packet = value;
        packet.component_id = this.component_id;
        this.value = value;

        this.sending = true;
        const requestID = ulid();
        this.requestID = requestID;
        const res = await REST.GET('check', packet)
        if(requestID != this.requestID){ // Confirm correct instance
            return false;
        }

        if (res.ok) {
            let data = await res.json();
            if ('bad' in data && data.bad === false) {
                this.valid = true;
                this.reason = '';
            } else {
                this.reason = data?.reason ?? 'Unknown error';
            }
            this.validated = true;
        } else {
            this.reason = 'Network error';
            KTIForm.error_modal.SetTmpTitle(`We're sorry - something went wrong.`)
			KTIForm.error_modal.Show(`We couldn't connect to the server. Please try again in a few minutes. If the problem continues, contact customer service at 800-222-0169.`)
        }
        this.sending = false;
        return this.valid;
    }

}